import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';
import { pluralized } from '~/components/filters';
import { useMessageHub } from '~/components/message-hub/useMessageHub';
import { useResponsiveLayout } from '~/composables/useResponsiveLayout';
import { useToasted } from '~/composables/useToasted';
import { FEATURES } from '~/provides/features';
import { contactsClient, messagesClient, scheduledMessagesClient, threadsClient } from '~/services';
import { catchRequestError } from '~/utils/request';
import { hasScope } from '~/utils/users';
import { HTTP_CONFLICT } from '~/support/HttpStatuses';
import {
    faCircleMinus,
    faCirclePlus,
    faEnvelope,
    faEnvelopeOpen,
    faMessageMinus,
    faUserPen,
    faUserPlus,
} from '@fortawesome/pro-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { createGlobalState } from '@vueuse/core';
import type { Agent } from '~/services/agents/agentsClient.types';
import type { Thread } from '~/types/thread.types';

interface BulkActionOptions {
    showUndo?: boolean;
}

interface MessageData {
    body: string;
    media_url: string | null;
    media_content_type: string | null;
    send_at: string | null;
}

interface SnippetData {
    body: string;
    media_url: string | null;
    media_content_type: string | null;
}

interface Action {
    id: string;
    icon: IconDefinition;
    label: string;
    color?: string;
    show: boolean;
    loading: boolean;
    handler: () => void;
}

interface PreviousAssignment {
    thread: Thread;
    previousAssigneeId: number | null;
    previousStatus: string;
}

export const useMessageHubBulkActions = createGlobalState(() => {
    const { selectedThreads, removeThreadSelection, agents } = useMessageHub() as {
        selectedThreads: Ref<Map<number, Thread>>;
        removeThreadSelection: (thread: Thread) => void;
        agents: Ref<Agent[]>;
    };
    const { isMd } = useResponsiveLayout();
    const toasted = useToasted();

    const affectedThreads: Ref<Thread[]> = ref([]);
    const success = ref('');
    const error = ref('');

    const closingThreads = ref(false);
    const openingThreads = ref(false);
    const markingAsRead = ref(false);
    const markingAsUnread = ref(false);
    const claimingThreads = ref(false);
    const assigningThreads = ref(false);
    const sendingMessage = ref(false);
    const unsubscribingContacts = ref(false);

    const closeWithSnippetModal = ref(false);
    const assignModal = ref(false);

    const undoAction: Ref<(() => void | Promise<void>) | null> = ref(null);
    const undoing = ref(false);

    const message: Ref<MessageData> = ref({
        body: '',
        media_url: null,
        media_content_type: null,
        send_at: null,
    });

    const executeUndo = async (): Promise<void> => {
        if (!undoAction.value || undoing.value) {
            return;
        }

        undoing.value = true;
        try {
            await undoAction.value();
            undoAction.value = null;
        } finally {
            undoing.value = false;
        }
    };

    const resetMessageData = (): void => {
        message.value.body = '';
        message.value.media_url = null;
        message.value.media_content_type = null;
        message.value.send_at = null;
    };

    const showNotification = (messageText: string, type: string, showUndo = false): void => {
        if (isMd.value) {
            if (type === 'success') {
                success.value = messageText;
            } else {
                error.value = messageText;
            }
        } else {
            const toastConfig: {
                title: string;
                type: string;
                action?: {
                    label: string;
                    onClick: () => Promise<void>;
                };
            } = {
                title: messageText,
                type: type,
            };

            if (showUndo) {
                toastConfig.action = {
                    label: 'Undo',
                    onClick: async () => {
                        toasted.dismiss(toastId);
                        await executeUndo();
                    },
                };
            }

            const toastId = toasted.show(toastConfig);
        }
    };

    const bulkCloseThreads = async (threads: Thread[], options: BulkActionOptions = {}): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        if (closingThreads.value || threads.length === 0) {
            return;
        }

        closingThreads.value = true;

        try {
            const promises = threads.map((thread) => {
                if (thread.status === 'closed') {
                    removeThreadSelection(thread);
                    return Promise.resolve(thread);
                }

                if (thread.group_id) {
                    return threadsClient
                        .groupThreadsState(thread.team_id, thread.group_id, thread.contact_id, 'closed')
                        .then(() => {
                            thread.status = 'closed';
                            removeThreadSelection(thread);
                            return thread;
                        });
                } else {
                    return threadsClient.threadsState(thread.team_id, thread.contact_id, 'closed').then(() => {
                        thread.status = 'closed';
                        removeThreadSelection(thread);
                        return thread;
                    });
                }
            });

            const results = await Promise.allSettled(promises);
            const successfulResults = results.filter((result) => result.status === 'fulfilled');
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successfulResults.length > 0) {
                const successMessage = `${pluralized('thread', successfulResults.length, true)} closed successfully.`;
                affectedThreads.value = successfulResults.map((r) => r.value);
                undoAction.value = () => bulkOpenThreads(affectedThreads.value, { showUndo: false });
                showNotification(successMessage, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                showNotification(`Failed to close ${pluralized('thread', errorCount, true)}.`, 'error');
            } else {
                error.value = '';
            }
        } finally {
            closingThreads.value = false;
        }
    };

    const bulkOpenThreads = async (threads: Thread[], options: BulkActionOptions = {}): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        if (openingThreads.value || threads.length === 0) {
            return;
        }

        openingThreads.value = true;

        try {
            const promises = threads.map((thread) => {
                if (thread.status === 'open') {
                    removeThreadSelection(thread);
                    return Promise.resolve(thread);
                }

                if (thread.group_id) {
                    return threadsClient
                        .groupThreadsState(thread.team_id, thread.group_id, thread.contact_id, 'open')
                        .then(() => {
                            thread.status = 'open';
                            removeThreadSelection(thread);
                            return thread;
                        });
                } else {
                    return threadsClient.threadsState(thread.team_id, thread.contact_id, 'open').then(() => {
                        thread.status = 'open';
                        removeThreadSelection(thread);
                        return thread;
                    });
                }
            });

            const results = await Promise.allSettled(promises);
            const successfulResults = results.filter((result) => result.status === 'fulfilled');
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successfulResults.length > 0) {
                const successMessage = `${pluralized('thread', successfulResults.length, true)} opened successfully.`;
                affectedThreads.value = successfulResults.map((r) => r.value);
                undoAction.value = () => bulkCloseThreads(affectedThreads.value, { showUndo: false });
                showNotification(successMessage, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                showNotification(`Failed to open ${pluralized('thread', errorCount, true)}.`, 'error');
            } else {
                error.value = '';
            }
        } finally {
            openingThreads.value = false;
        }
    };

    const bulkMarkAsRead = async (threads: Thread[], options: BulkActionOptions = {}): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        if (markingAsRead.value || threads.length === 0) {
            return;
        }

        markingAsRead.value = true;

        try {
            const promises = threads.map((thread) => {
                if (!thread.unread || thread.unread === 0) {
                    removeThreadSelection(thread);
                    return Promise.resolve(thread);
                }

                if (thread.group_id) {
                    return threadsClient
                        .groupThreadsCheckpointSave(thread.team_id, thread.group_id, thread.contact_id)
                        .then(() => {
                            thread.unread = 0;
                            removeThreadSelection(thread);
                            return thread;
                        });
                } else {
                    return threadsClient.threadsCheckpointSave(thread.team_id, thread.contact_id).then(() => {
                        thread.unread = 0;
                        removeThreadSelection(thread);
                        return thread;
                    });
                }
            });

            const results = await Promise.allSettled(promises);
            const successfulResults = results.filter((result) => result.status === 'fulfilled');
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successfulResults.length > 0) {
                const successMessage = `${pluralized('thread', successfulResults.length, true)} marked as read.`;
                affectedThreads.value = successfulResults.map((r) => r.value);
                undoAction.value = () => bulkMarkAsUnread(affectedThreads.value, { showUndo: false });
                showNotification(successMessage, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                showNotification(`Failed to mark ${pluralized('thread', errorCount, true)} as read.`, 'error');
            } else {
                error.value = '';
            }
        } finally {
            markingAsRead.value = false;
        }
    };

    const bulkMarkAsUnread = async (threads: Thread[], options: BulkActionOptions = {}): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        if (markingAsUnread.value || threads.length === 0) {
            return;
        }

        markingAsUnread.value = true;

        try {
            const promises = threads.map((thread) => {
                if (thread.unread && thread.unread > 0) {
                    removeThreadSelection(thread);
                    return Promise.resolve(thread);
                }

                if (thread.group_id) {
                    return threadsClient
                        .groupThreadsCheckpointDelete(thread.team_id, thread.group_id, thread.contact_id)
                        .then(() => {
                            thread.unread = 1;
                            removeThreadSelection(thread);
                            return thread;
                        });
                } else {
                    return threadsClient.threadsCheckpointDelete(thread.team_id, thread.contact_id).then(() => {
                        thread.unread = 1;
                        removeThreadSelection(thread);
                        return thread;
                    });
                }
            });

            const results = await Promise.allSettled(promises);
            const successfulResults = results.filter((result) => result.status === 'fulfilled');
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successfulResults.length > 0) {
                const successMessage = `${pluralized('thread', successfulResults.length, true)} marked as unread.`;
                affectedThreads.value = successfulResults.map((r) => r.value);
                undoAction.value = () => bulkMarkAsRead(affectedThreads.value, { showUndo: false });
                showNotification(successMessage, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                showNotification(`Failed to mark ${pluralized('thread', errorCount, true)} as unread.`, 'error');
            } else {
                error.value = '';
            }
        } finally {
            markingAsUnread.value = false;
        }
    };

    const bulkClaimThreads = async (threads: Thread[], options: BulkActionOptions = {}): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        if (claimingThreads.value || threads.length === 0) {
            return;
        }

        const previousAssignments: PreviousAssignment[] = threads.map((thread) => ({
            thread: { ...thread },
            previousAssigneeId: thread.assignee_id,
            previousStatus: thread.status,
        }));

        claimingThreads.value = true;

        try {
            const claimPromises = threads.map(async (thread) => {
                if (thread.assignee_id === Spark.state.user.id) {
                    removeThreadSelection(thread);
                    return Promise.resolve(thread);
                }

                if (thread.status !== 'open') {
                    if (thread.group_id) {
                        await threadsClient.groupThreadsState(
                            thread.team_id,
                            thread.group_id,
                            thread.contact_id,
                            'open'
                        );
                    } else {
                        await threadsClient.threadsState(thread.team_id, thread.contact_id, 'open');
                    }
                }

                if (thread.group_id) {
                    return threadsClient
                        .groupThreadsAssignToSelf(thread.team_id, thread.group_id, thread.contact_id)
                        .then(() => {
                            removeThreadSelection(thread);
                            return thread;
                        });
                } else {
                    return threadsClient.threadsAssignToSelf(thread.team_id, thread.contact_id).then(() => {
                        removeThreadSelection(thread);
                        return thread;
                    });
                }
            });

            const results = await Promise.allSettled(claimPromises);
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successCount > 0) {
                const successMessage = `${pluralized('thread', successCount, true)} claimed successfully.`;
                undoAction.value = async () => {
                    for (const { thread, previousAssigneeId, previousStatus } of previousAssignments) {
                        if (previousAssigneeId === null) {
                            await unassignThread(thread, { showUndo: false });
                        }
                        if (previousStatus === 'closed') {
                            await bulkCloseThreads([thread], { showUndo: false });
                        }
                    }
                };
                showNotification(successMessage, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                showNotification(`Failed to claim ${pluralized('thread', errorCount, true)}.`, 'error');
            } else {
                error.value = '';
            }
        } finally {
            claimingThreads.value = false;
        }
    };

    const unassignThread = async (thread: Thread, options: BulkActionOptions = {}): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        const request = thread.group_id
            ? threadsClient.groupThreadsUnclaim(thread.team_id, thread.group_id, thread.contact_id)
            : threadsClient.threadsUnclaim(thread.team_id, thread.contact_id);

        await request;
        removeThreadSelection(thread);
    };

    const getAssigneeName = (assigneeId: number | string): string => {
        if (assigneeId === 'unassigned') {
            return 'Unassigned';
        }
        if (assigneeId === Spark.state.user.id) {
            return 'You';
        }
        const agent = agents.value.find((agent) => agent.id === assigneeId);
        return agent ? agent.name : 'Unknown Agent';
    };

    const handleBulkAssign = async (
        assigneeId: number | string,
        threads: Thread[],
        options: BulkActionOptions = {}
    ): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        if (assigningThreads.value || threads.length === 0) {
            return;
        }

        const previousAssignments: PreviousAssignment[] = threads.map((thread) => ({
            thread: { ...thread },
            previousAssigneeId: thread.assignee_id,
            previousStatus: thread.status,
        }));

        assigningThreads.value = true;

        try {
            const assignPromises = threads.map(async (thread) => {
                if (thread.status !== 'open') {
                    if (thread.group_id) {
                        await threadsClient.groupThreadsState(
                            thread.team_id,
                            thread.group_id,
                            thread.contact_id,
                            'open'
                        );
                    } else {
                        await threadsClient.threadsState(thread.team_id, thread.contact_id, 'open');
                    }
                }

                if (assigneeId === 'unassigned') {
                    if (thread.group_id) {
                        return threadsClient
                            .groupThreadsUnclaim(thread.team_id, thread.group_id, thread.contact_id)
                            .then(() => {
                                removeThreadSelection(thread);
                                return thread;
                            });
                    } else {
                        return threadsClient.threadsUnclaim(thread.team_id, thread.contact_id).then(() => {
                            removeThreadSelection(thread);
                            return thread;
                        });
                    }
                } else {
                    const assignToSelf = assigneeId === Spark.state.user.id;
                    if (thread.group_id) {
                        if (assignToSelf) {
                            return threadsClient
                                .groupThreadsAssignToSelf(thread.team_id, thread.group_id, thread.contact_id)
                                .then(() => {
                                    removeThreadSelection(thread);
                                    return thread;
                                });
                        } else {
                            return threadsClient
                                .groupThreadsAssignTo(thread.team_id, thread.group_id, thread.contact_id, {
                                    assignee_id: assigneeId as number,
                                })
                                .then(() => {
                                    removeThreadSelection(thread);
                                    return thread;
                                });
                        }
                    } else {
                        if (assignToSelf) {
                            return threadsClient.threadsAssignToSelf(thread.team_id, thread.contact_id).then(() => {
                                removeThreadSelection(thread);
                                return thread;
                            });
                        } else {
                            return threadsClient
                                .threadsAssignTo(thread.team_id, thread.contact_id, {
                                    assignee_id: assigneeId as number,
                                })
                                .then(() => {
                                    removeThreadSelection(thread);
                                    return thread;
                                });
                        }
                    }
                }
            });

            const results = await Promise.allSettled(assignPromises);
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successCount > 0) {
                const message =
                    assigneeId === 'unassigned'
                        ? `${pluralized('thread', successCount, true)} unassigned successfully.`
                        : `${pluralized('thread', successCount, true)} assigned to ${getAssigneeName(
                              assigneeId
                          )} successfully.`;

                undoAction.value = async () => {
                    for (const { thread, previousAssigneeId, previousStatus } of previousAssignments) {
                        if (previousAssigneeId === null) {
                            await unassignThread(thread, { showUndo: false });
                        } else {
                            await handleBulkAssign(previousAssigneeId, [thread], { showUndo: false });
                        }
                        if (previousStatus === 'closed') {
                            await bulkCloseThreads([thread], { showUndo: false });
                        }
                    }
                };
                showNotification(message, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                const message =
                    assigneeId === 'unassigned'
                        ? `Failed to unassign ${pluralized('thread', errorCount, true)}.`
                        : `Failed to assign ${pluralized('thread', errorCount, true)} to ${getAssigneeName(
                              assigneeId
                          )}.`;
                showNotification(message, 'error');
            } else {
                error.value = '';
            }

            assignModal.value = false;
        } finally {
            assigningThreads.value = false;
        }
    };

    const closeWithSnippet = async (
        { body, media_url, media_content_type }: SnippetData,
        options: BulkActionOptions = {}
    ): Promise<void> => {
        options.showUndo = options.showUndo ?? true;

        if (selectedThreads.value.size === 0) {
            return;
        }

        const threads = Array.from(selectedThreads.value.values());
        const threadSnapshots = threads.map((t) => ({ ...t }));

        try {
            const closePromises = threads.map(async (thread) => {
                const payload = {
                    contact_id: thread.contact_id,
                    body,
                    media_url,
                    media_content_type,
                };

                if (thread.group_id) {
                    await messagesClient.groupMessageCreate(thread.team_id, thread.group_id, payload);
                } else {
                    await messagesClient.createV3(thread.team_id, payload);
                }

                if (thread.group_id) {
                    return threadsClient
                        .groupThreadsState(thread.team_id, thread.group_id, thread.contact_id, 'closed')
                        .then(() => {
                            removeThreadSelection(thread);
                            return thread;
                        });
                } else {
                    return threadsClient.threadsState(thread.team_id, thread.contact_id, 'closed').then(() => {
                        removeThreadSelection(thread);
                        return thread;
                    });
                }
            });

            const results = await Promise.allSettled(closePromises);
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successCount > 0) {
                const successMessage = `${pluralized('thread', successCount, true)} closed with snippet.`;
                affectedThreads.value = threadSnapshots.filter((_, i) => results[i]?.status === 'fulfilled');
                undoAction.value = () => bulkOpenThreads(affectedThreads.value, { showUndo: false });
                showNotification(successMessage, 'success', options.showUndo);
            } else {
                success.value = '';
            }

            if (errorCount > 0) {
                showNotification(`Failed to close ${pluralized('thread', errorCount, true)} with snippet.`, 'error');
            } else {
                error.value = '';
            }

            closeWithSnippetModal.value = false;
        } catch (e) {
            error.value = catchRequestError(e);
        }
    };

    const bulkUnsubscribeContacts = async (threads: Thread[]): Promise<void> => {
        if (unsubscribingContacts.value || threads.length === 0) {
            return;
        }

        unsubscribingContacts.value = true;

        try {
            const unsubscribePromises = threads.map(async (thread) => {
                const marketingPromise = contactsClient
                    .contactSubscriptionOptOut(thread.team_id, thread.contact_id, 'marketing')
                    .catch((error) => {
                        if (error?.response?.status !== HTTP_CONFLICT) {
                            throw error;
                        }
                        return thread;
                    });

                const transactionalPromise = contactsClient
                    .contactSubscriptionOptOut(thread.team_id, thread.contact_id, 'transactional')
                    .catch((error) => {
                        if (error?.response?.status !== HTTP_CONFLICT) {
                            throw error;
                        }
                        return thread;
                    });

                return Promise.all([marketingPromise, transactionalPromise]).then(() => {
                    removeThreadSelection(thread);
                    return thread;
                });
            });

            const results = await Promise.allSettled(unsubscribePromises);
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successCount > 0) {
                success.value = `${pluralized('contact', successCount, true)} unsubscribed successfully.`;
            } else {
                success.value = '';
            }
            if (errorCount > 0) {
                error.value = `Failed to unsubscribe ${pluralized('contact', errorCount, true)}.`;
            } else {
                error.value = '';
            }
        } finally {
            unsubscribingContacts.value = false;
        }
    };

    const sendBulkMessage = async (): Promise<void> => {
        if (sendingMessage.value || selectedThreads.value.size === 0 || !message.value.body.trim()) {
            return;
        }

        sendingMessage.value = true;
        const threads = Array.from(selectedThreads.value.values());

        try {
            const messagePromises = threads.map((thread) => {
                const payload = {
                    contact_id: thread.contact_id,
                    body: message.value.body,
                    media_url: message.value.media_url,
                    media_content_type: message.value.media_content_type,
                };

                if (thread.group_id) {
                    return messagesClient
                        .groupMessageCreate(thread.team_id, thread.group_id, payload)
                        .then((result) => {
                            removeThreadSelection(thread);
                            return result;
                        });
                } else {
                    return messagesClient.createV3(thread.team_id, payload).then((result) => {
                        removeThreadSelection(thread);
                        return result;
                    });
                }
            });

            const results = await Promise.allSettled(messagePromises);
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successCount > 0) {
                success.value = `Message sent to ${pluralized('contact', successCount, true)}.`;
                resetMessageData();
            } else {
                success.value = '';
            }
            if (errorCount > 0) {
                error.value = `Failed to send message to ${pluralized('contact', errorCount, true)}.`;
            } else {
                error.value = '';
            }
        } finally {
            sendingMessage.value = false;
        }
    };

    const scheduleBulkMessage = async (scheduleAt: string): Promise<void> => {
        if (sendingMessage.value || selectedThreads.value.size === 0 || !message.value.body.trim()) {
            return;
        }

        sendingMessage.value = true;
        const threads = Array.from(selectedThreads.value.values());

        try {
            const schedulePromises = threads.map((thread) => {
                const payload = {
                    contact_id: thread.contact_id,
                    body: message.value.body,
                    media_url: message.value.media_url,
                    media_content_type: message.value.media_content_type,
                    send_at: scheduleAt,
                };

                return scheduledMessagesClient
                    .createScheduledMessage(
                        { teamId: thread.team_id, groupId: thread.group_id },
                        thread.contact_id,
                        payload
                    )
                    .then((result) => {
                        removeThreadSelection(thread);
                        return result;
                    });
            });

            const results = await Promise.allSettled(schedulePromises);
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successCount > 0) {
                success.value = `Message scheduled for ${pluralized('contact', successCount, true)}.`;
                resetMessageData();
            } else {
                success.value = '';
            }
            if (errorCount > 0) {
                error.value = `Failed to schedule message for ${pluralized('contact', errorCount, true)}.`;
            } else {
                error.value = '';
            }
        } finally {
            sendingMessage.value = false;
        }
    };

    const openAssignModal = (): void => {
        assignModal.value = true;
    };

    const hasOpenThreads: ComputedRef<boolean> = computed(() => {
        return (
            selectedThreads.value.size === 0 ||
            Array.from(selectedThreads.value.values()).some((thread: Thread) => thread.status === 'open')
        );
    });

    const hasClosedThreads: ComputedRef<boolean> = computed(() => {
        return (
            selectedThreads.value.size === 0 ||
            Array.from(selectedThreads.value.values()).some((thread: Thread) => thread.status !== 'open')
        );
    });

    const hasUnreadThreads: ComputedRef<boolean> = computed(() => {
        return (
            selectedThreads.value.size === 0 ||
            Array.from(selectedThreads.value.values()).some((thread: Thread) => thread.unread > 0)
        );
    });

    const hasReadThreads: ComputedRef<boolean> = computed(() => {
        return (
            selectedThreads.value.size === 0 ||
            Array.from(selectedThreads.value.values()).some((thread: Thread) => !thread.unread || thread.unread === 0)
        );
    });

    const hasUnclaimedThreads: ComputedRef<boolean> = computed(() => {
        return (
            selectedThreads.value.size === 0 ||
            Array.from(selectedThreads.value.values()).some(
                (thread: Thread) => thread.assignee_id !== Spark.state.user.id
            )
        );
    });

    const actions: ComputedRef<Action[]> = computed(() => {
        const selectedThreadsArray: Thread[] = Array.from(selectedThreads.value.values());

        if (FEATURES?.simple_message_hub) {
            return [
                {
                    id: 'unread',
                    icon: faEnvelope,
                    label: 'Unread',
                    show: hasClosedThreads.value,
                    loading: openingThreads.value,
                    handler: () => bulkOpenThreads(selectedThreadsArray),
                },
                {
                    id: 'read',
                    icon: faEnvelopeOpen,
                    label: 'Read',
                    show: hasOpenThreads.value,
                    loading: closingThreads.value,
                    handler: () => bulkCloseThreads(selectedThreadsArray),
                },
            ];
        }

        return [
            {
                id: 'close',
                icon: faCircleMinus,
                label: 'Close',
                color: 'text-rose-500',
                show: hasOpenThreads.value,
                loading: closingThreads.value,
                handler: () => bulkCloseThreads(selectedThreadsArray),
            },
            {
                id: 'close-with-snippet',
                icon: faMessageMinus,
                label: 'Close w/ Snippet',
                show: hasOpenThreads.value && isMd.value,
                loading: false,
                handler: () => (closeWithSnippetModal.value = true),
            },
            {
                id: 'open',
                icon: faCirclePlus,
                label: 'Open',
                show: hasClosedThreads.value,
                loading: openingThreads.value,
                handler: () => bulkOpenThreads(selectedThreadsArray),
            },
            {
                id: 'unread',
                icon: faEnvelope,
                label: 'Mark Unread',
                show: hasReadThreads.value,
                loading: markingAsUnread.value,
                handler: () => bulkMarkAsUnread(selectedThreadsArray),
            },
            {
                id: 'read',
                icon: faEnvelopeOpen,
                label: 'Mark Read',
                show: hasUnreadThreads.value,
                loading: markingAsRead.value,
                handler: () => bulkMarkAsRead(selectedThreadsArray),
            },
            {
                id: 'claim',
                icon: faUserPlus,
                label: 'Claim',
                show: hasScope('threads:claim') && !hasScope('threads:assign') && hasUnclaimedThreads.value,
                loading: claimingThreads.value,
                handler: () => bulkClaimThreads(selectedThreadsArray),
            },
            {
                id: 'assign',
                icon: faUserPen,
                label: 'Assign',
                show: hasScope('threads:assign'),
                loading: assigningThreads.value,
                handler: openAssignModal,
            },
        ];
    });

    return {
        bulkCloseThreads,
        bulkOpenThreads,
        bulkMarkAsRead,
        bulkMarkAsUnread,
        bulkClaimThreads,
        handleBulkAssign,
        closeWithSnippet,
        bulkUnsubscribeContacts,
        sendBulkMessage,
        scheduleBulkMessage,

        actions,
        closeWithSnippetModal,
        assignModal,

        message,
        resetMessageData,
        sendingMessage,
        unsubscribingContacts,

        success,
        error,
        affectedThreads,

        executeUndo,
        undoAction,
        undoing,
    };
});
